import { cloneObject } from './Cloneable.js';
import { SimpleError } from '@common/errors';
import { isAutoEncoder } from '../helpers/isAutoEncoder.js';
export function isPatchable(object) {
    if (!object) {
        return false;
    }
    return !!object.patch;
}
export function patchContainsChanges(patch, model, context) {
    const patched = model.patch(patch);
    return JSON.stringify(patched.encode(context)) != JSON.stringify(model.encode(context));
}
export class PatchMap extends Map {
    _isPatch = true;
    _isPatchMap = true;
    applyTo(obj) {
        if (isPatchMap(obj)) {
            // Combine instead of normal logic
            const clone = new PatchMap(obj);
            for (const [key, value] of this.entries()) {
                if (value === null) {
                    clone.set(key, null);
                    continue;
                }
                if (value === undefined) {
                    continue;
                }
                const original = obj.get(key);
                if (original === null) {
                    // Has been deleted higher up
                    if (isPatch(value)) {
                        continue;
                    }
                    clone.set(key, value);
                    continue;
                }
                if (original === undefined) {
                    clone.set(key, value);
                    continue;
                }
                clone.set(key, patchObject(original, value, { allowAutoDefaultValue: true }));
            }
            return clone;
        }
        const clone = new Map(obj);
        for (const [key, value] of this.entries()) {
            if (value === null) {
                clone.delete(key);
                continue;
            }
            if (value === undefined) {
                continue;
            }
            const original = obj.get(key);
            const patched = patchObject(original, value, { allowAutoDefaultValue: true });
            if (original === undefined && patched === undefined) {
                // Don't copy it: this is an empty patch to an item that does not exist
                continue;
            }
            clone.set(key, patched);
        }
        return clone;
    }
    clone() {
        // Deep clone self
        const clone = new PatchMap();
        for (const [key, value] of this.entries()) {
            clone.set(key, cloneObject(value));
        }
        return clone;
    }
}
export function isPatchMap(obj) {
    return typeof obj === 'object' && obj !== null && obj._isPatchMap;
}
export function isPatchableArray(obj) {
    return typeof obj === 'object' && obj !== null && obj._isPatchableArray;
}
export function isPatch(obj) {
    if (isAutoEncoder(obj)) {
        // Instance type could be different
        return obj.isPatch();
    }
    if (isPatchMap(obj)) {
        return true;
    }
    if (isPatchableArray(obj)) {
        return true;
    }
    return false;
}
export function isEmptyPatch(patch) {
    if (patch === undefined) {
        return true;
    }
    if (patch === null) {
        return false;
    }
    if (Array.isArray(patch)) {
        // Can override array
        return false;
    }
    if (isPatchableArray(patch)) {
        return patch.changes.length === 0;
    }
    if (isPatchMap(patch)) {
        return patch.size === 0;
    }
    if (patch instanceof Map) {
        return false;
    }
    if (typeof patch === 'object') {
        if (Object.keys(patch).length === 0) {
            return true;
        }
        return false;
    }
    return false;
}
/**
 * Applies a patch or replacement. Undefined keeps the original value; null clears it.
 * An absent nested AutoEncoder requires a default before an object patch can be applied.
 * Map patches can use automatic decoder defaults; missing array item IDs are not created.
 */
export function patchObject(obj, patch, options) {
    if (patch === undefined) {
        // When a property is set to undefined, we always ignore it, always. You can never set something to undefined.
        // Use null instead.
        return obj;
    }
    let empty = (obj === undefined || obj === null);
    if (empty) {
        if (isPatchableArray(patch)) {
            // Patch on optional array: ignore if empty patch, else fake empty array patch
            if (patch.changes.length === 0) {
                return obj;
            }
            const patched = patch.applyTo([]);
            if (patched.length === 0) {
                // Nothing changed, keep it undefined or null
                return obj;
            }
            return patched;
        }
        if (isPatchMap(patch)) {
            // Patch on optional array: ignore if empty patch, else fake empty array patch
            if (patch.size === 0) {
                return obj;
            }
            const patched = patch.applyTo(new Map());
            if (patched.size === 0) {
                // Nothing changed, keep it undefined or null
                return obj;
            }
            return patched;
        }
        // Only default if not a patchable array or patchable map
        if (options?.getDefaultValue !== undefined) {
            const d = options.getDefaultValue();
            if (d !== undefined && d !== null) {
                obj = d;
                empty = false;
            }
        }
    }
    if (isAutoEncoder(patch) && patch.isPut()) {
        // Instance type could be different
        return patch;
    }
    if (isPatchable(obj)) {
        if (patch === null) {
            return null;
        }
        return obj.patch(patch);
    }
    if (obj instanceof Map && isPatchMap(patch)) {
        return patch.applyTo(obj);
    }
    if (Array.isArray(obj)) {
        // Check if patch is a patchable array
        if (isPatchableArray(patch)) {
            return patch.applyTo(obj);
        }
        // What happens when an array field is set?
        // This can only happen when the autocoder is not identifieable, but
        // technically also in other cases if typescript doesn't check types
        // we just take over the new values and 'remove' all old elements
        return patch;
    }
    // Note: only when null, if undefined we allow
    if (empty && isAutoEncoder(patch) && patch.isPatch()) {
        // Check if we have a default where we can start.
        if (options?.allowAutoDefaultValue && patch.static.putType) {
            const def = (patch.static.putType).getDefaultValue();
            if (def) {
                return def.patch(patch);
            }
        }
        // Throw an error
        // You cannot patch null or undefined with a patchable object
        throw new SimpleError({
            code: 'cannot_patch_null',
            message: 'Cannot patch an object or property that is null or undefined and has no default value. You can only send a full object (a put) to replace it.',
        });
    }
    return patch;
}
//# sourceMappingURL=Patchable.js.map